import React, { useEffect, useState } from "react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { Mail, Loader2 } from "lucide-react";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const PROFILE_LABEL = { entreprise: "Entreprise", particulier: "Particulier" };
const INTEREST_LABEL = {
  vie: "Assurance vie",
  per: "PER",
  prevoyance: "Prévoyance",
  ird: "IRD",
  autre: "Autre",
};

export default function AdminContacts() {
  const { authFetch } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    authFetch("get", "/admin/contacts")
      .then((res) => setItems(res.data.items))
      .finally(() => setLoading(false));
  }, [authFetch]);

  return (
    <div data-testid="admin-contacts-root">
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={16} strokeWidth={1.5} className="text-[#b08d57]" />
          <span className="text-[10px] tracking-[0.32em] uppercase text-[#b08d57]">
            Boîte de réception
          </span>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl">
          {items.length} demande{items.length > 1 ? "s" : ""} de contact
        </h1>
      </div>

      {loading ? (
        <div className="text-[#efeade]/50 flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="text-[#efeade]/50">Aucune demande pour l&apos;instant.</div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`admin-contact-${c.id}`}
              className="border border-[#efeade]/10 bg-[#0a192f]"
            >
              <button
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                className="w-full flex flex-col md:flex-row md:items-center gap-3 md:gap-6 p-5 text-left hover:bg-white/5 transition-colors"
              >
                <span
                  className={`text-[10px] tracking-[0.22em] uppercase px-3 py-1 self-start ${
                    c.profile === "entreprise"
                      ? "bg-[#b08d57] text-[#0a192f]"
                      : "bg-[#efeade]/10 text-[#efeade]"
                  }`}
                >
                  {PROFILE_LABEL[c.profile] || c.profile}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#efeade]">{c.name}</div>
                  <div className="text-xs text-[#efeade]/50 truncate">
                    {c.email}{c.company ? ` · ${c.company}` : ""}
                  </div>
                </div>
                <div className="text-xs text-[#efeade]/40">
                  {INTEREST_LABEL[c.interest] || c.interest} · {formatDate(c.created_at)}
                </div>
              </button>
              {expanded === c.id && (
                <div className="px-5 py-6 border-t border-[#efeade]/10 space-y-3 text-sm">
                  <Row label="Téléphone" value={c.phone || "—"} />
                  <div>
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#b08d57] mb-2">
                      Message
                    </div>
                    <p className="text-[#efeade]/90 whitespace-pre-line leading-relaxed">
                      {c.message}
                    </p>
                  </div>
                  <div className="pt-2">
                    <a
                      href={`mailto:${c.email}?subject=Re: votre demande — Phénix Patrimoine et Assurance`}
                      className="inline-block bg-[#b08d57] text-[#0a192f] px-5 py-3 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[#f0b74e] transition-colors"
                    >
                      Répondre par e-mail
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#b08d57] mb-1">
        {label}
      </div>
      <div className="text-[#efeade]/90">{value}</div>
    </div>
  );
}
